import { useEffect, useState } from "react";
import { Col, Row, Form, Button } from "react-bootstrap";
import PageLayout from "../../../layouts/PageLayout";
import { Link, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import RescueTableComponent from "../../../components/tables/RescueTableComponent";
import PulseLoader from "react-spinners/PulseLoader";
import Swal from "sweetalert2";

export default function AllRescuedFamilies() {
  const AuthToken = localStorage.getItem("AuthUser");
  const roleId = JSON.parse(localStorage.getItem("user")).role_id;

  const location = useLocation();
  const navigate = useNavigate();

  // ✅ STATES
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [familyDonor, setFamilyDonor] = useState([]);

  const [searchQuery, setSearchQuery] = useState("");

  const [formState, setFormState] = useState({
    from_date: "",
    to_date: "",
  });

  const [error, setError] = useState("");

  const [pagination, setPagination] = useState({
    current_page: 1,
    last_page: 1,
    total: 0,
    required_page: 1,
  });

  // ✅ FETCH DONORS (ROLE 3)
  useEffect(() => {
    if (roleId === 3) {
      axios.defaults.headers.common["Authorization"] = `Bearer ${AuthToken}`;

      axios
        .get(`${process.env.REACT_APP_API_URL}/family-donors`)
        .then((res) => setFamilyDonor(res.data.donors))
        .catch((err) => console.error(err));
    }
  }, [roleId, AuthToken]);

  // ✅ MAIN API CALL
  const fetchData = async (page = 1, filters = {}, search = "") => {
    setLoading(true);

    try {
      axios.defaults.headers.common["Authorization"] = `Bearer ${AuthToken}`;

      const params = {
        page,
        ...filters,
      };

      if (search) params.search = search;

      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/rescue-list`,
        { params }
      );

      const peoples = response.data.peoples;

      setData(peoples.data || []);
      setPagination({
        current_page: peoples.current_page,
        last_page: peoples.last_page,
        total: peoples.total,
        required_page: peoples.current_page,
      });
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  // ✅ INITIAL LOAD + URL PAGE
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const page = Number(params.get("page")) || 1;

    fetchData(page, formState, searchQuery);
  }, [location.search]);

  // ✅ SEARCH (Debounce)
  // useEffect(() => {
  //   const delay = setTimeout(() => {
  //     fetchData(1, formState, searchQuery);
  //     navigate(`?page=1`);
  //   }, 500);

  //   return () => clearTimeout(delay);
  // }, [searchQuery]);

  const handleSearchClick = () => {
    fetchData(1, formState, searchQuery);
    navigate(`?page=1`);
  };
  // ✅ DATE INPUT CHANGE
  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormState((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // ✅ APPLY FILTER
  const handleApplyFilter = () => {
    const { from_date, to_date } = formState;

    if (!from_date || !to_date) {
      setError("Both dates required");
      return;
    }

    if (new Date(to_date) < new Date(from_date)) {
      setError("Invalid date range");
      return;
    }

    setError("");
    fetchData(1, formState, searchQuery);
    navigate(`?page=1`);
  };

  // ✅ CLEAR FILTER
  const handleClearFilter = () => {
    setFormState({ from_date: "", to_date: "" });
    setError("");
    fetchData(1, {}, searchQuery);
  };

  // ✅ PAGINATION
  const handlePageChange = (page) => {
    fetchData(page, formState, searchQuery);
    navigate(`?page=${page}`);
  };

  // ✅ UPDATE DONOR
  const onChangeRole = async (event, id) => {
    try {
      axios.defaults.headers.common["Authorization"] = `Bearer ${AuthToken}`;

      const res = await axios.post(
        `${process.env.REACT_APP_API_URL}/update-family-donor-id/${id}`,
        {
          family_donor_id: event.target.value || null,
        }
      );

      if (res.data.success) {
        Swal.fire("Success", res.data.message, "success");
      }
    } catch (error) {
      console.error(error);
    }
  };

  // ✅ MAP DATA
  const mappedData = data.map((person) => ({
    id: person.id,
    fsf_id: person.f_number,
    beneficiary_name: person.beneficiary_name,
    beneficiary_cnic: person.cnic,
    partner_name: person.identifier?.name,
    organization_church: person.identifier?.org,
    verified_by: person.verifier?.name,
    sub_donor_id: person.sub_donor_id,
    rescue_date: person.rescue_date,
    stage: person.stage?.name,
    progress: person.full_progress,
    action: {
      edit: "edit",
      view: "visibility",
    },
  }));

  return (
    <PageLayout>
      {/* HEADER */}
      <div className="mc-card p-3">
        <Row className="g-3 align-items-center">
          <Col md={3}>
            <Form.Control
              type="date"
              name="from_date"
              value={formState.from_date}
              onChange={handleChange}
            />
          </Col>

          <Col md={3}>
            <Form.Control
              type="date"
              name="to_date"
              value={formState.to_date}
              onChange={handleChange}
            />
          </Col>

          <Col md={2}>
            <Button onClick={handleApplyFilter}>
              Apply
            </Button>
          </Col>

          <Col md={2}>
            <Button variant="secondary" onClick={handleClearFilter}>
              Clear
            </Button>
          </Col>
        </Row>

        {error && <p className="text-danger mt-2">{error}</p>}
      </div>

      {/* TABLE */}
      <div className="mc-card mt-3">
        {loading ? (
          <div className="text-center p-5">
            <PulseLoader color="#077834" />
          </div>
        ) : (
          <RescueTableComponent
            thead={
              roleId === 3
                ? [
                  "Beneficiary",
                  "Family Donor",
                  "Partner",
                  "Verified By",
                  "Rescue Date",
                  "Status",
                  "Progress",
                  "Action",
                ]
                : [
                  "Beneficiary",
                  "Partner",
                  "Verified By",
                  "Rescue Date",
                  "Status",
                  "Progress",
                  "Action",
                ]
            }
            tbody={mappedData}
            roleId={roleId}
            donors={familyDonor}
            onChangeRole={onChangeRole}
            searchQuery={searchQuery}
            onSearch={setSearchQuery}
            onSearchClick={handleSearchClick}   // ✅ ADD THIS

          />
        )}
      </div>

      {/* PAGINATION */}
      <div className="d-flex justify-content-center gap-3 mt-3">
        <Button
          disabled={pagination.current_page === 1}
          onClick={() => handlePageChange(pagination.current_page - 1)}
        >
          Prev
        </Button>

        <span>
          Page {pagination.current_page} of {pagination.last_page}
        </span>

        <Button
          disabled={pagination.current_page === pagination.last_page}
          onClick={() => handlePageChange(pagination.current_page + 1)}
        >
          Next
        </Button>
      </div>
    </PageLayout>
  );
}